import type { CheckoutFailure } from "@/lib/checkout-flow";

/**
 * One timing beacon per guest checkout run: how long the session, Stripe.js
 * and the `redirectToCheckout` call took from the click, or whether the page
 * went away, failed or stalled before the redirect was called. It is sent at
 * the redirect call, at a failure, when the page is hidden first, or at the
 * deadline, whichever comes first. Server-side PostHog sees
 * the session being created, nothing after it; client PostHog runs in memory
 * before consent and drops automated browsers.
 *
 * The server signs a short-lived token per guest session (none when
 * CHECKOUT_TIMING_SECRET is unset, which turns this off). Without a token
 * nothing is recorded or sent. All of it observes only: every hook swallows
 * its own errors, nothing is awaited, and a stale run (see checkout-lock)
 * sends nothing. Failure modes: tests/checkout-timing/failure-modes.md.
 */

export const CHECKOUT_TIMING_URL = "/api/checkout-timing";
/**
 * From the click, like the checkout lock's 20 s, and under it, so a run that
 * never leaves still reports while it is current, even after a slow session.
 */
export const CHECKOUT_TIMING_DEADLINE_MS = 15_000;

export type CheckoutTimingOutcome =
  | "redirect_called"
  | "failed"
  | "hidden_before_redirect"
  | "stalled";
export type CheckoutTimingStage =
  | "session_returned"
  | "stripe_loaded"
  | "redirect_called";

export interface CheckoutTimingHooks {
  sessionReturned(sessionId: string, token: string | undefined): void;
  stripeLoaded(): void;
  /** Right after `redirectToCheckout` was called, before it settles. */
  redirectCalled(): void;
  failed(stage: CheckoutFailure["failure_stage"]): void;
}

export interface CheckoutTimingEnv {
  now: () => number;
  sendBeacon?: (url: string, body: string) => boolean;
  fetch?: (url: string, init: RequestInit) => Promise<unknown>;
  /** Calls `listener` when the page is hidden or unloaded; returns a remover. */
  onHide: (listener: () => void) => () => void;
  setTimer: (fn: () => void, ms: number) => () => void;
}

const noopHooks: CheckoutTimingHooks = {
  sessionReturned: () => undefined,
  stripeLoaded: () => undefined,
  redirectCalled: () => undefined,
  failed: () => undefined,
};

export function browserCheckoutTimingEnv(): CheckoutTimingEnv | null {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }
  return {
    now: () => performance.now(),
    sendBeacon:
      typeof navigator.sendBeacon === "function"
        ? (url, body) => navigator.sendBeacon(url, body)
        : undefined,
    fetch:
      typeof window.fetch === "function"
        ? (url, init) => window.fetch(url, init)
        : undefined,
    onHide: (listener) => {
      const onVisibility = () => {
        if (document.visibilityState === "hidden") listener();
      };
      window.addEventListener("pagehide", listener);
      document.addEventListener("visibilitychange", onVisibility);
      return () => {
        window.removeEventListener("pagehide", listener);
        document.removeEventListener("visibilitychange", onVisibility);
      };
    },
    setTimer: (fn, ms) => {
      const id = window.setTimeout(fn, ms);
      return () => window.clearTimeout(id);
    },
  };
}

const quietly = (fn: () => void): void => {
  try {
    fn();
  } catch {
    // Timing is optional; it must never touch the checkout.
  }
};

/** Call at the click. `isCurrent` is the checkout lock's stale-run check. */
export function createCheckoutTiming(
  isCurrent: () => boolean,
  givenEnv?: CheckoutTimingEnv | null,
): CheckoutTimingHooks {
  // Runs at the click, before the session: nothing here may throw (row 26).
  let resolvedEnv: CheckoutTimingEnv | null;
  let startedAt: number;
  try {
    resolvedEnv =
      givenEnv === undefined ? browserCheckoutTimingEnv() : givenEnv;
    if (!resolvedEnv) return noopHooks;
    startedAt = resolvedEnv.now();
  } catch {
    return noopHooks;
  }
  const env: CheckoutTimingEnv = resolvedEnv;

  let sessionId: string | undefined;
  let token: string | undefined;
  let stage: CheckoutTimingStage = "session_returned";
  const marks: Partial<Record<`${CheckoutTimingStage}_ms`, number>> = {};
  let isDone = false;
  let removeHide: (() => void) | undefined;
  let clearDeadline: (() => void) | undefined;

  const elapsed = () => Math.max(0, Math.round(env.now() - startedAt));

  const dispose = () => {
    isDone = true;
    const remove = [removeHide, clearDeadline];
    removeHide = undefined;
    clearDeadline = undefined;
    remove.forEach((fn) => fn && quietly(fn));
  };

  const send = (fields: Record<string, unknown>) => {
    try {
      const beaconBody = JSON.stringify({ ...fields, transport: "beacon" });
      if (env.sendBeacon?.(CHECKOUT_TIMING_URL, beaconBody)) return;
    } catch {
      // Fall through to fetch.
    }
    try {
      if (!env.fetch) return;
      // No cookies: the route reads only the body.
      Promise.resolve(
        env.fetch(CHECKOUT_TIMING_URL, {
          method: "POST",
          body: JSON.stringify({ ...fields, transport: "fetch" }),
          headers: { "Content-Type": "text/plain;charset=UTF-8" },
          keepalive: true,
          credentials: "omit",
        }),
      ).catch(() => undefined);
    } catch {
      // Blocked or unavailable: the event is simply missing.
    }
  };

  const finish = (
    outcome: CheckoutTimingOutcome,
    extra: Record<string, unknown> = {},
  ) =>
    quietly(() => {
      if (isDone || !token || !sessionId) return;
      if (!isCurrent()) {
        // A stale run sends nothing (checkout-lock).
        dispose();
        return;
      }
      const sentMs = elapsed();
      dispose();
      send({
        token,
        session_id: sessionId,
        outcome,
        stage,
        ...marks,
        ...extra,
        sent_ms: sentMs,
      });
    });

  const onHide = () =>
    quietly(() => {
      const hiddenMs = elapsed();
      finish(
        stage === "redirect_called" ? "redirect_called" : "hidden_before_redirect",
        { hidden_ms: hiddenMs },
      );
    });

  const mark = (next: CheckoutTimingStage) =>
    quietly(() => {
      if (isDone || !token) return;
      stage = next;
      marks[`${next}_ms`] = elapsed();
    });

  return {
    sessionReturned: (id, sessionToken) =>
      quietly(() => {
        if (isDone || token || !sessionToken) return;
        sessionId = id;
        token = sessionToken;
        mark("session_returned");
        const onDeadline = () =>
          finish(stage === "redirect_called" ? "redirect_called" : "stalled");
        const remainingMs = CHECKOUT_TIMING_DEADLINE_MS - elapsed();
        if (remainingMs <= 0) {
          onDeadline();
          return;
        }
        removeHide = env.onHide(onHide);
        clearDeadline = env.setTimer(onDeadline, remainingMs);
      }),
    stripeLoaded: () => mark("stripe_loaded"),
    // Sent at once, queued before the page starts leaving: WebKit may never
    // run pagehide/visibilitychange on the page Stripe.js navigates away
    // from (failure mode 29), so the success path can't wait for them.
    redirectCalled: () => {
      mark("redirect_called");
      finish("redirect_called");
    },
    failed: (failureStage) =>
      quietly(() => {
        if (failureStage === "session_creation") return;
        finish("failed", { failure_stage: failureStage });
      }),
  };
}
